import { useT } from '@/core/I18nProvider';

import { BEAT } from './branch-beats';
import { BranchCard } from './BranchCard';

import type { Branch } from './story-data';
import type { ReactElement } from 'react';

/**
 * One branch of the story: a bare composer that types a question, sends it, and
 * extrudes a result card upward out of itself.
 *
 * The order matters and is the whole illusion. The composer arrives FIRST and
 * alone — a single input floating in black — so the card that follows reads as
 * something the question produced rather than as a panel that faded in. The
 * seam between them (a bright rule that grows and then dies) is the moment of
 * production; without it the card just appears above an unrelated input.
 */
export function StoryBranch({ branch }: { readonly branch: Branch }): ReactElement {
  const { t } = useT();
  const { at, placement } = branch;
  const ask = t(branch.askKey);

  return (
    <div
      data-story-node
      className="absolute w-[290px]"
      style={{
        insetInlineStart: placement.x,
        bottom: placement.bottom,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        animation: `sxDrift${(branch.id.length % 3) + 1} ${9.5 + placement.x / 400}s ease-in-out 12.6s infinite`,
      }}
    >
      {/* The card's brand bloom — it blooms as the card lands, then settles to
          a low ambient glow so six lit cards do not wash the stage out. */}
      <div
        aria-hidden="true"
        data-story-deco
        className="pointer-events-none absolute -top-6 bottom-3.5 opacity-0"
        style={{
          insetInline: -26,
          background: `radial-gradient(closest-side, ${branch.accent}66, transparent 74%)`,
          filter: 'blur(28px)',
          animation: `sxBloom 1.9s cubic-bezier(.16,1,.3,1) ${(at + BEAT.card).toFixed(2)}s forwards`,
        }}
      />

      {/* The result card. */}
      <div
        className="relative box-border flex h-[252px] flex-col overflow-hidden rounded-2xl border border-[rgba(17,24,39,.09)] p-3.5"
        style={{
          background: 'linear-gradient(180deg,#fff,#fbfcff)',
          boxShadow:
            '0 26px 56px -20px rgba(4,7,20,.94), 0 8px 20px -10px rgba(4,7,20,.62),' +
            '0 0 0 1px rgba(255,255,255,.15), 0 0 44px -12px rgba(96,132,255,.36),' +
            'inset 0 1px 0 rgba(255,255,255,.95)',
          animation: `sxUnfold .8s cubic-bezier(.16,1,.3,1) ${(at + BEAT.card).toFixed(2)}s backwards`,
        }}
      >
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[3px] origin-[left_center] rtl:origin-[right_center]"
          style={{
            background: `linear-gradient(90deg, ${branch.accent}, ${branch.accentTo})`,
            animation: `sxGrowX .7s cubic-bezier(.16,1,.3,1) ${(at + BEAT.card + 0.15).toFixed(2)}s backwards`,
          }}
        />

        <div className="flex flex-none items-center gap-2">
          <span
            className="inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border py-0.5 pe-2.5 ps-[7px] text-xs font-medium leading-tight text-[#3d4553]"
            style={{
              borderColor: `${branch.accent}2e`,
              background: `${branch.accent}10`,
              animation: `sxPopS .45s cubic-bezier(.34,1.56,.64,1) ${(at + BEAT.card + 0.25).toFixed(2)}s backwards`,
            }}
          >
            <span
              aria-hidden="true"
              className="size-1.5 flex-none rounded-full"
              style={{ background: branch.accent, boxShadow: `0 0 0 2.5px ${branch.accent}10` }}
            />
            {t(branch.badgeKey)}
          </span>
          <span
            className="ms-auto flex-none whitespace-nowrap font-mono rtl:font-sans text-[10px] uppercase tracking-[0.08em] text-[#6e6e77] rtl:normal-case rtl:tracking-normal"
            style={{
              animation: `sxFade .4s ease ${(at + BEAT.card + 0.35).toFixed(2)}s backwards`,
            }}
          >
            {t(branch.metaKey)}
          </span>
        </div>

        <BranchCard branch={branch} />
      </div>

      {/* The seam: the moment the composer produced the card. */}
      <div
        aria-hidden="true"
        className="my-2 h-0.5 origin-center rounded-sm"
        style={{
          background:
            'linear-gradient(90deg,transparent,var(--bd-cyan) 28%,var(--bd-purple) 52%,var(--bd-violet) 72%,transparent)',
          boxShadow: '0 0 14px rgba(120,140,255,.45)',
          animation: `sxSeam .9s ease ${(at + BEAT.seam).toFixed(2)}s both`,
        }}
      />

      {/* The composer. */}
      <div
        data-story-composer
        className="flex items-center gap-2 rounded-[14px] border border-[rgba(17,24,39,.09)] p-1.5 ps-3"
        style={{
          background: 'linear-gradient(180deg,#fff,#fbfcfe)',
          boxShadow:
            '0 16px 36px -18px rgba(4,7,20,.88), 0 0 0 1px rgba(255,255,255,.15),' +
            '0 0 26px -10px rgba(96,132,255,.3), inset 0 1px 0 rgba(255,255,255,.95)',
          animation: `sxUnfoldX .6s cubic-bezier(.16,1,.3,1) ${at.toFixed(2)}s backwards`,
        }}
      >
        <span className="flex min-w-0 flex-1 items-center gap-0.5">
          <span
            data-type
            className="inline-block overflow-hidden whitespace-nowrap text-[13.5px] text-[#111827]"
            style={{
              animation: `sxTypeW .65s steps(${Math.max(8, ask.length)},end) ${(at + BEAT.type).toFixed(2)}s backwards`,
            }}
          >
            {ask}
          </span>
          <span
            aria-hidden="true"
            className="inline-block h-3.5 w-[1.5px] flex-none bg-[#2563eb]"
            style={{
              animation:
                `sxFade .1s linear ${(at + BEAT.type - 0.05).toFixed(2)}s backwards,` +
                `sxCaret .85s steps(1) ${(at + BEAT.type).toFixed(2)}s infinite,` +
                `sxOut .18s linear ${(at + BEAT.send).toFixed(2)}s forwards`,
            }}
          />
        </span>
        <span
          aria-hidden="true"
          className="grid size-7 flex-none place-items-center rounded-xl text-xs text-white rtl:-scale-x-100"
          style={{
            background: 'linear-gradient(155deg,#2563eb,#1d4ed8)',
            boxShadow: '0 4px 14px -6px rgba(37,99,235,.38)',
            animation: `sxFlash .75s ease-out ${(at + BEAT.send).toFixed(2)}s backwards`,
          }}
        >
          ↑
        </span>
      </div>
    </div>
  );
}
